import { useCallback, useEffect, useRef, useState } from "react";
import useApi from "./useApi";
import {
  loadWorkflowInstances,
  WorkflowInstancesLoadResult,
} from "../services/workflowService";
import { WorkflowInstancesQuery } from "../types/workflowDto";
import {
  toWorkflowInstanceListItem,
  WorkflowInstanceListItem,
  WorkflowInstanceStatus,
  WORKFLOW_STATUS_FILTER_TO_API,
} from "../features/workflows/workflowInstancesData";

export interface WorkflowInstancesFilters {
  search: string;
  definitionRootId: number | "all";
  status: WorkflowInstanceStatus | "all";
  /** Epic 80431 - "Include test runs": other people's Builder test runs in the viewer's scope. */
  includeTest?: boolean;
}

export interface WorkflowInstancesListState {
  rows: WorkflowInstanceListItem[];
  total: number;
  /** The first page of a (re)query is in flight - the grid shows its loading overlay. */
  loading: boolean;
  /** The last request failed; whatever was loaded before it is still shown. */
  error: boolean;
  /** 403 - no Instances permission. Rows are cleared, never left from an earlier response. */
  forbidden: boolean;
}

const EMPTY: WorkflowInstancesListState = { rows: [], total: 0, loading: true, error: false, forbidden: false };

/**
 * Epic 80431 - the Instances table's server-paged, role-scoped list (scope=auto: the server picks
 * from the caller's Portal roles). A filter change starts again at page 1; `loadMore` appends the
 * next page as the grid scrolls. A failed load keeps the last good rows (the epic: "a transient
 * failure must not blank the roster") and offers `retry`; a 403 clears them.
 */
const useWorkflowInstancesList = (filters: WorkflowInstancesFilters, pageSize: number, enabled = true) => {
  const { executeApi } = useApi();
  const [state, setState] = useState<WorkflowInstancesListState>(enabled ? EMPTY : { ...EMPTY, loading: false });
  const [page, setPage] = useState(1);
  const [attempt, setAttempt] = useState(0);
  const inFlight = useRef(false);

  const { search, definitionRootId, status } = filters;
  const includeTest = Boolean(filters.includeTest);
  const filterKey = `${search}|${definitionRootId}|${status}|${includeTest}`;
  const lastFilterKey = useRef(filterKey);

  useEffect(() => {
    // No Instances access: make no request at all (it would only 403).
    if (!enabled) {
      setState({ ...EMPTY, loading: false });
      return undefined;
    }

    // A new query always starts from page 1. Resetting here, not in a separate effect, means the
    // new filters are never fetched against the old page number (which would append page N of a
    // different query to the rows already shown).
    if (lastFilterKey.current !== filterKey) {
      lastFilterKey.current = filterKey;
      if (page !== 1) {
        setPage(1);
        return undefined;
      }
    }

    let active = true;
    const firstPage = page === 1;
    inFlight.current = true;
    setState((current) => ({ ...current, loading: firstPage, error: false }));

    const query: WorkflowInstancesQuery = {
      search,
      status: WORKFLOW_STATUS_FILTER_TO_API[status],
      definitionRootId: definitionRootId === "all" ? undefined : definitionRootId,
      ...(includeTest ? { includeTest } : {}),
      page,
      pageSize,
    };

    executeApi<WorkflowInstancesQuery, WorkflowInstancesLoadResult>(loadWorkflowInstances, query)
      .then((result) => {
        if (!active) return;
        if (!result) {
          setState((current) => ({ ...current, loading: false, error: true }));
          return;
        }
        if (result.kind === "forbidden") {
          setState({ rows: [], total: 0, loading: false, error: false, forbidden: true });
          return;
        }
        const rows = result.page.items.map((item) => toWorkflowInstanceListItem(item));
        setState((current) => {
          if (firstPage) return { rows, total: result.page.total, loading: false, error: false, forbidden: false };
          // A row can shift pages while the user scrolls (a case updated meanwhile); never list it twice.
          const loaded = new Set(current.rows.map((row) => row.id));
          return {
            rows: [...current.rows, ...rows.filter((row) => !loaded.has(row.id))],
            total: result.page.total,
            loading: false,
            error: false,
            forbidden: false,
          };
        });
      })
      .finally(() => {
        if (active) inFlight.current = false;
      });

    return () => {
      active = false;
    };
  }, [enabled, executeApi, filterKey, search, definitionRootId, status, includeTest, page, pageSize, attempt]);

  const loadMore = useCallback(() => {
    if (inFlight.current || state.loading || state.error || state.forbidden) return;
    if (state.rows.length >= state.total) return;
    setPage((current) => current + 1);
  }, [state]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  return { ...state, loadMore, retry };
};

export default useWorkflowInstancesList;
